import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";
//! Move to a new DnD!
import { Droppable, Draggable, DragDropContext } from "react-beautiful-dnd";

import { useStateSelector } from "utils/field";
import { getAllComponents, getAllSubcomponents } from "utils/enclosure/components";
import { required, maxLength } from "utils/fieldValidation";
import { agCodeCompLabelMap, agCodeCompMap, codeCompMap, codeSort, standardLayerLogic, layerSort } from "utils/codes";
import { getModelDataComponentPath, getCodeDrawerEmbodiedComponentType } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import uDefCodeTemplate from "features/Model/Enclosure/Templates/standardUDefCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Field from "components/Field";
import Button from "components/Button";
import MultiSelect from "components/Input/MultiSelect";
import EmptyState from "components/EmptyState";
import Checkbox from "components/Input/Checkbox";
import Tooltip from "components/Tooltip";
import AddLayer from "../AddLayer";
import ContinuousMedium from "./ContinuousMedium";
import ContinuousInsulation from "./ContinuousInsulation";
import WoodFraming from "./WoodFraming";
import SteelFraming from "./SteelFraming";
import Strapping from "./Strapping";
import { EmbodiedCarbonCalculationsSummary } from "components/EmbodiedCarbonGrade";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";

import classes from "../style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

import Info from "assets/images/icons/JSX/info";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getUDefRValue } = enclosureActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const descriptionValidation = [maxLength(100)];

const getLayerType = {
    continuousMedium: ContinuousMedium,
    continuousInsulation: ContinuousInsulation,
    woodFraming: WoodFraming,
    steelFraming: SteelFraming,
    strapping: Strapping,
};

const StandardLayers = React.memo(
    ({
        change,
        componentType,
        fieldAccessor,
        isCodeLibrary = false,
        handleClose,
        handleUdefSave,
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        componentId,
        removeFormErrors,
        upgradeWhereToAddCode = {},
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");
        const [dragActive, toggleDragActive] = useState(false);

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { udefStandard = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const {
            continuousMedium: continuousMediumLayers = {},
            continuousInsulation: continuousInsulationLayers = {},
            woodFraming: woodFramingLayers = {},
            steelFraming: steelFramingLayers = {},
            strapping: strappingLayers = {},
        } = useStateSelector(`${drawerAccessor}.uDefCode.layers`) || {};

        const medium = Object.keys(continuousMediumLayers).map((key) => ({
            ...continuousMediumLayers[key],
            id: key,
            type: "continuousMedium",
        }));

        const insulation = Object.keys(continuousInsulationLayers).map((key) => ({
            ...continuousInsulationLayers[key],
            id: key,
            type: "continuousInsulation",
        }));

        const woodFraming = Object.keys(woodFramingLayers).map((key) => ({
            ...woodFramingLayers[key],
            id: key,
            type: "woodFraming",
        }));

        const steelFraming = Object.keys(steelFramingLayers).map((key) => ({
            ...steelFramingLayers[key],
            id: key,
            type: "steelFraming",
        }));

        const strapping = Object.keys(strappingLayers).map((key) => ({
            ...strappingLayers[key],
            id: key,
            type: "strapping",
        }));

        const codeLayers = [...medium, ...insulation, ...woodFraming, ...steelFraming, ...strapping].sort(layerSort);

        const numLayers = codeLayers.length;

        const { codeRef: editingCodeRef = null, isLibCode = false } = udefStandard;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath(componentType, fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];
        const embodiedComponentType = getCodeDrawerEmbodiedComponentType(componentType);

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        useEffect(() => {
            const initialValues = {
                uDefCode: isEmpty(udefStandard) ? uDefCodeTemplate : udefStandard,
                addToLibrary: isEmpty(udefStandard) ? true : isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        const onDragStart = () => {};
        const onDragEnd = (result) => {
            const { destination, source, draggableId } = result;
            const { index: destinationIndex } = destination;
            const { index: sourceIndex } = source;
            const newDraggableIndex = destinationIndex + 1;

            const [type, id] = draggableId.split(".");

            codeLayers.map((layer, index) => {
                const { id: layerId = "", type: layerType = "" } = layer;
                let layerIndex = index + 1;

                // Dragged layer
                if (id === layerId) {
                    change(`${drawerAccessor}.uDefCode.layers.${type}.${id}.layer`, newDraggableIndex);
                    return true;
                }

                // Layers with unchanged indexes: outside of the source/destination before and after dragging
                if (
                    (layerIndex <= sourceIndex && layerIndex < newDraggableIndex) ||
                    (layerIndex > sourceIndex && layerIndex > newDraggableIndex)
                ) {
                    change(`${drawerAccessor}.uDefCode.layers.${layerType}.${layerId}.layer`, layerIndex);
                    return true;
                }

                // Layers that were less than source, but greater than destination (ie. draggable was put in front)
                if (layerIndex <= sourceIndex && layerIndex >= newDraggableIndex) {
                    layerIndex = layerIndex + 1;
                }

                // Layers that were greater than source, but were less than destination (ie. draggable was put behind)
                if (layerIndex > sourceIndex && layerIndex <= newDraggableIndex) {
                    layerIndex = layerIndex - 1;
                }

                change(`${drawerAccessor}.uDefCode.layers.${layerType}.${layerId}.layer`, layerIndex);
            });
        };

        const compType = codeCompMap[componentType] || "wall";
        const availableComponents =
            compType === "floorHeader"
                ? getAllSubcomponents("floorHeader", components, fieldAccessor).sort(codeSort)
                : getAllComponents(compType, components, fieldAccessor).sort(codeSort);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        return (
            <div className={classes.uDefCodeWrapper}>
                {/* <h3>User Defined Code</h3> */}
                <InputRow
                    gridTemplate={
                        CodeTypeSelect ||
                        (!isCodeLibrary && !isUpgradeComponent && Object.keys(agCodeCompMap).includes(componentType))
                            ? "1fr 1fr 1fr"
                            : "1fr 1fr"
                    }
                    style={{ alignItems: "flex-end" }}
                >
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.uDefCode.label`}
                        label="User Defined Code Label"
                        placeholder="Enter Code Label"
                        validate={labelValidation}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.uDefCode.description`}
                        label="Description"
                        placeholder="Enter Code Description"
                        validate={descriptionValidation}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && Object.keys(agCodeCompMap).includes(componentType) && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <CodeLibraryDestination
                                    drawerAccessor={drawerAccessor}
                                    editingCodeRef={editingCodeRef}
                                    isLibCode={isLibCode}
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                        </>
                    )}
                </InputRow>
                <div className={`${classes.title} ${classes.layers}`}>
                    <h4>
                        Layers{""}
                        <div
                            className={classes.layerTooltip}
                            data-for="layersTooltip"
                            data-html
                            data-tip={`<span style="display:block;max-width:18rem;">It is best practice to arrange layers from exterior (lowest layer) to interior (highest layer)</span>`}
                        >
                            <Info />
                        </div>
                    </h4>
                    <Tooltip id="layersTooltip" />
                    <div className={classes.layersButtons}>
                        <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                        <AddLayer
                            formName={`${drawerAccessor}.uDefCode`}
                            change={change}
                            numLayers={numLayers}
                            componentType={componentType}
                            options={[
                                {
                                    value: "continuousMedium",
                                    label: "Continuous Medium",
                                },
                                {
                                    value: "continuousInsulation",
                                    label: "Continuous Insulation",
                                },
                                {
                                    value: "woodFraming",
                                    label: "Wood Framing",
                                },
                                {
                                    value: "steelFraming",
                                    label: "Steel Framing",
                                },
                                {
                                    value: "strapping",
                                    label: "Strapping",
                                },
                            ]}
                        />
                    </div>
                </div>
                <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
                    <Droppable droppableId="standard-code" type="standard-code">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                className={classNames(classes.droppableArea, {
                                    [classes.draggedOver]: snapshot.isDraggingOver,
                                })}
                                {...provided.droppableProps}
                            >
                                {isEmpty(codeLayers) && <EmptyState title="No layers to display" style={{}} />}
                                {codeLayers.map((layer, ind) => {
                                    const { id, type, ...rest } = layer;
                                    const LayerType = getLayerType[type];

                                    return (
                                        <Draggable draggableId={`${type}.${id}`} index={ind} key={id}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className={classNames(classes.draggableComponent, {
                                                        [classes.draggedOver]: snapshot.isDraggingOver,
                                                    })}
                                                >
                                                    <LayerType
                                                        type={type}
                                                        key={ind}
                                                        id={id}
                                                        value={rest}
                                                        change={change}
                                                        isDragging={snapshot.isDragging}
                                                        dragHandleProps={provided.dragHandleProps}
                                                        dragActive={dragActive}
                                                        accessor={`${drawerAccessor}.uDefCode.layers.${type}.${id}`} // HELP
                                                        drawerAccessor={drawerAccessor}
                                                        componentType={componentType}
                                                        barMetric={barMetric}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={codeLayers}
                    componentType={embodiedComponentType}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                    isUdefCode={true}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            handleClose();
                            removeFormErrors(`${drawerAccessor}.uDefCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                fieldAccessor,
                                editingCodeRef,
                                uid,
                                componentType,
                                isCodeLibrary,
                                handleUdefSave,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                componentId,
                                upgradeWhereToAddCode,
                            });
                            // Clear udef code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "udefStandard")),
    handleSubmit: ({
        change,
        codeFields,
        fieldAccessor,
        editingCodeRef,
        uid,
        componentType,
        isCodeLibrary,
        componentId,
        handleUdefSave,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            uDefCode = {},
            addToLibrary = false,
            orgLibraryId = "",
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        //Close drawer
        handleUdefSave();

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        standardLayerLogic({
            change,
            uDefCode,
            componentType,
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getUDefRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            componentId,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardLayers);
